/**
 * Pure helpers for campaign kind and record state labels (KAN-593).
 */
import type { CampaignKind } from '@/types/dataset';

const KIND_LABELS: Record<CampaignKind, string> = {
  voice: 'Обзвон',
  telegram_outreach: 'Telegram',
};

/** Missing kind is treated as voice (old lists). */
export const kindLabel = (kind?: CampaignKind): string =>
  KIND_LABELS[kind ?? 'voice'] ?? KIND_LABELS.voice;

/** Options of the kind filter: "all" (no ?kind=) plus every kind. */
export const CAMPAIGN_KIND_OPTIONS: ReadonlyArray<{
  value: 'all' | CampaignKind;
  label: string;
}> = [
  { value: 'all', label: 'Все' },
  { value: 'voice', label: KIND_LABELS.voice },
  { value: 'telegram_outreach', label: KIND_LABELS.telegram_outreach },
];

/** Kinds a new campaign can be created with (no "all"), voice first. */
export const CAMPAIGN_KIND_CHOICES = CAMPAIGN_KIND_OPTIONS.filter(
  (o): o is { value: CampaignKind; label: string } => o.value !== 'all'
);

const LIST_SUBTITLES: Record<'all' | CampaignKind, string> = {
  all: 'Кампании: обзвон и Telegram',
  voice: 'Кампании холодного обзвона',
  telegram_outreach: 'Кампании Telegram-рассылки',
};

/** Page subtitle that follows the kind filter of the campaigns list. */
export const campaignListSubtitle = (filter: 'all' | CampaignKind): string =>
  LIST_SUBTITLES[filter];

const BASE_STATE_LABELS: Record<string, string> = {
  new: 'Новый',
  queued: 'В очереди',
  pending: 'Ожидает',
  contacted: 'Обработан',
  do_not_call: 'Не звонить',
  failed: 'Ошибка',
  calling: 'Дозвон',
  no_answer: 'Нет ответа',
  answered: 'Отвечено',
};

const TELEGRAM_STATE_LABELS: Record<string, string> = {
  messaged: 'Написали',
  replied: 'Ответил',
  not_in_telegram: 'Нет в Telegram',
  // KAN-999: с клиентом уже идёт переписка, первое сообщение не отправлялось.
  already_in_dialog: 'Уже в диалоге',
  failed: 'Ошибка отправки',
  do_not_contact: 'Не писать',
};

/** Russian label for a record state; unknown states are returned as is. */
export function recordStateLabel(state: string, kind?: CampaignKind): string {
  if (kind === 'telegram_outreach' && state in TELEGRAM_STATE_LABELS) {
    return TELEGRAM_STATE_LABELS[state];
  }
  return BASE_STATE_LABELS[state] ?? TELEGRAM_STATE_LABELS[state] ?? state;
}

/** Действие оператора над записью: в какое состояние перевести и как подписать. */
export interface RecordStopAction {
  state: string;
  label: string;
  successMessage: string;
}

const STOP_ACTIONS: Record<CampaignKind, RecordStopAction> = {
  voice: {
    state: 'do_not_call',
    label: 'Не звонить',
    successMessage: 'Запись помечена "Не звонить"',
  },
  telegram_outreach: {
    state: 'do_not_contact',
    label: 'Не писать',
    successMessage: 'Запись помечена "Не писать"',
  },
};

/** KAN-594: ручное "не беспокоить" для вида кампании (пустой вид = обзвон). */
export const recordStopAction = (kind?: CampaignKind): RecordStopAction =>
  STOP_ACTIONS[kind ?? 'voice'] ?? STOP_ACTIONS.voice;
